/**
 * Hariom ERP - Reports & Analytics
 * Charts come from real demand-forecast data; every Download button (and
 * "Export All") produces a real Excel file built from the live module stores.
 */
import React from 'react'
import { Download, FileText } from 'lucide-react'
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import * as XLSX from 'xlsx'
import toast from 'react-hot-toast'
import { useForecastStore } from '../../store/forecastStore'
import { useInventoryStore } from '../../store/inventoryStore'
import { useProductionStore } from '../../store/productionStore'
import { useSalesOrderStore } from '../../store/salesStore'
import { useQCStore } from '../../store/qcStore'
import { useInvoiceStore } from '../../store/financeStore'
import { exportRowsToExcel } from '../../utils/exportExcel'

const TREND_MONTHS: [string, string][] = [
  ['MARCH', 'Mar'], ['APR', 'Apr'], ['MAY', 'May'], ['JUNE', 'Jun'], ['JULY', 'Jul'], ['AUG', 'Aug'],
]

type Row = Record<string, string | number>

const Reports: React.FC = () => {
  const { contractors } = useForecastStore()
  const { items: products } = useInventoryStore()
  const { items: prodOrders } = useProductionStore()
  const { items: salesOrders } = useSalesOrderStore()
  const { items: qcTests } = useQCStore()
  const { items: invoices } = useInvoiceStore()

  // Charts from real forecast data.
  const forecastData = TREND_MONTHS.map(([key, label]) => {
    const forecast = contractors.reduce((s, c) => s + (c.monthlyData.find(m => m.month === key)?.forecast || 0), 0)
    const actual = contractors.reduce((s, c) => s + (c.monthlyData.find(m => m.month === key)?.actual || 0), 0)
    return { month: label, forecast, actual, fulfillment: forecast > 0 ? Math.round((actual / forecast) * 100) : 0 }
  })

  // Each report builds its rows from the live stores.
  const builders: Record<string, () => Row[]> = {
    'Monthly Demand Forecast Report': () => forecastData.map(d => ({ Month: d.month, Forecast: d.forecast, Actual: d.actual, 'Fulfillment %': d.fulfillment })),
    'Contractor Performance Summary': () => contractors.map(c => ({ Contractor: c.name, Forecast: c.totalForecast, Actual: c.totalActual, Diff: c.totalDiff, Status: c.status })),
    'Inventory Stock Status Report': () => products.map(p => ({ Code: p.code, Product: p.name, Category: p.category, Stock: p.stock, Min: p.min, Max: p.max, Price: p.price, Location: p.location })),
    'Production Efficiency Report': () => prodOrders.map(o => ({ 'Order No': o.orderNo, Product: o.product, Planned: o.planned, Actual: o.actual, 'Efficiency %': o.planned ? Math.round((o.actual / o.planned) * 100) : 0, Status: o.status })),
    'Quality Compliance Report': () => qcTests.map(t => ({ Batch: t.batch, Product: t.product, Test: t.testType, Standard: t.standard, Measured: t.measured, Result: t.result, Date: t.date })),
    'Sales Order Summary': () => salesOrders.map(o => ({ 'Order No': o.orderNo, Customer: o.customer, Items: o.items, Amount: o.amount, Status: o.status, Date: o.date })),
    'P&L Statement (Monthly)': () => invoices.map(i => ({ Invoice: i.invoiceNo, Type: i.type, Party: i.party, Total: i.total, Status: i.status, Date: i.date })),
    'Receivables Ageing Report': () => invoices.filter(i => i.type === 'sales' && i.status !== 'paid').map(i => ({ Invoice: i.invoiceNo, Party: i.party, Total: i.total, Paid: i.paid, Outstanding: i.total - i.paid, Due: i.due })),
  }

  const REPORT_LIST = [
    { name: 'Monthly Demand Forecast Report', module: 'Forecast',  updated: '2026-06-13', type: 'Excel' },
    { name: 'Contractor Performance Summary', module: 'Forecast',  updated: '2026-06-13', type: 'Excel' },
    { name: 'Inventory Stock Status Report',  module: 'Inventory', updated: '2026-06-13', type: 'Excel' },
    { name: 'Production Efficiency Report',    module: 'Production',updated: '2026-06-10', type: 'Excel' },
    { name: 'Quality Compliance Report',      module: 'QC',        updated: '2026-06-12', type: 'Excel' },
    { name: 'Sales Order Summary',            module: 'Sales',     updated: '2026-06-13', type: 'Excel' },
    { name: 'P&L Statement (Monthly)',        module: 'Finance',   updated: '2026-06-01', type: 'Excel' },
    { name: 'Receivables Ageing Report',      module: 'Finance',   updated: '2026-06-13', type: 'Excel' },
  ]

  const downloadOne = (name: string) => {
    const rows = builders[name]?.() ?? []
    if (!rows.length) { toast.error('No data for this report yet'); return }
    exportRowsToExcel(rows, name.slice(0, 28), `Hariom_${name.replace(/[^a-z0-9]+/gi, '_')}`)
    toast.success(`Downloaded: ${name}`)
  }

  const exportAll = () => {
    const wb = XLSX.utils.book_new()
    let any = false
    REPORT_LIST.forEach((r, idx) => {
      const rows = builders[r.name]?.() ?? []
      if (rows.length) {
        any = true
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), `${idx + 1}_${r.module}`.slice(0, 31))
      }
    })
    if (!any) { toast.error('No data to export yet — add data in the modules first'); return }
    XLSX.writeFile(wb, `Hariom_All_Reports_${new Date().toISOString().slice(0, 10)}.xlsx`)
    toast.success('All reports exported')
  }

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports &amp; Analytics</h1>
          <p className="page-subtitle">Comprehensive business intelligence across all modules</p>
        </div>
        <button onClick={exportAll} className="btn-secondary text-sm flex items-center gap-2"><Download size={14}/> Export All</button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-semibold text-steel-800 mb-4">Forecast Fulfillment Rate (%)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`}/>
              <Tooltip formatter={(v: number) => [`${v}%`]}/>
              <Line type="monotone" dataKey="fulfillment" stroke="#C41E3A" strokeWidth={2} dot={{ r: 4 }} name="Fulfillment %"/>
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="font-semibold text-steel-800 mb-4">Volume: Forecast vs Actual (Tons)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
              <XAxis dataKey="month" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }}/>
              <Tooltip formatter={(v: number) => [`${v} T`]}/>
              <Legend/>
              <Bar dataKey="forecast" name="Forecast" fill="#94a3b8" radius={[3, 3, 0, 0]}/>
              <Bar dataKey="actual"   name="Actual"   fill="#C41E3A" radius={[3, 3, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-steel-100"><h3 className="font-semibold text-steel-800">Available Reports</h3></div>
        <table className="w-full">
          <thead><tr className="bg-steel-50">
            <th className="table-header">Report Name</th><th className="table-header">Module</th>
            <th className="table-header">Last Updated</th><th className="table-header">Format</th>
            <th className="table-header text-center">Download</th>
          </tr></thead>
          <tbody>
            {REPORT_LIST.map((r, i) => (
              <tr key={i} className="table-row-hover">
                <td className="table-cell font-medium text-steel-800 flex items-center gap-2">
                  <FileText size={14} className="text-steel-400 flex-shrink-0"/>{r.name}
                </td>
                <td className="table-cell"><span className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded">{r.module}</span></td>
                <td className="table-cell text-xs text-steel-500">{r.updated}</td>
                <td className="table-cell text-xs text-steel-500">{r.type}</td>
                <td className="table-cell text-center">
                  <button onClick={() => downloadOne(r.name)} className="flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 mx-auto">
                    <Download size={11}/> Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Reports
